import { useContext, useState } from 'react';
import Scrollbar from 'src/components/Scrollbar';
import { SidebarContext } from 'src/contexts/SidebarContext';

import {
  Box,
  Drawer,
  alpha,
  styled,
  Divider,
  useTheme,
  lighten,
  darken,
  Tooltip,
  Typography,
  Link,
  Menu,
  IconButton
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import SidebarMenu from './SidebarMenu';
import Logo from 'src/components/LogoSign';

interface SidebarWrapperProps {
  collapsed?: boolean;
}

const SidebarWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'collapsed'
})<SidebarWrapperProps>(
  ({ theme, collapsed }) => `
        width: ${collapsed ? theme.sidebar.collapsedWidth : theme.sidebar.width};
        min-width: ${collapsed ? theme.sidebar.collapsedWidth : theme.sidebar.width};
        color: ${theme.colors.alpha.trueWhite[70]};
        position: relative;
        z-index: 7;
        height: 100%;
        padding-bottom: 68px;
        transition: width 0.3s ease, min-width 0.3s ease;
`
);

const CollapseButton = styled(IconButton)(
  ({ theme }) => `
    position: absolute;
    right: -12px;
    top: 20px;
    width: 24px;
    height: 24px;
    background: ${theme.colors.primary.main};
    color: ${theme.colors.alpha.trueWhite[100]};
    border-radius: 50%;
    z-index: 10;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);

    &:hover {
      background: ${theme.colors.primary.dark};
    }

    .MuiSvgIcon-root {
      font-size: 16px;
    }
`
);

function Sidebar() {
  const { sidebarToggle, toggleSidebar, sidebarCollapsed, toggleSidebarCollapse } = useContext(SidebarContext);
  const closeSidebar = () => toggleSidebar();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorElDrawer, setAnchorElDrawer] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const openDrawer = Boolean(anchorElDrawer);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClickDrawer = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElDrawer(event.currentTarget);
  };

  const handleCloseDrawer = () => {
    setAnchorElDrawer(null);
  };

  return (
    <>
      <SidebarWrapper
        collapsed={sidebarCollapsed}
        sx={{
          display: {
            xs: 'none',
            lg: 'inline-block'
          },
          position: 'fixed',
          left: 0,
          top: 0,
          background:
            theme.palette.mode === 'dark'
              ? alpha(lighten(theme.header.background, 0.1), 0.5)
              : darken(theme.colors.alpha.black[100], 0.5),
          boxShadow:
            theme.palette.mode === 'dark' ? theme.sidebar.boxShadow : 'none'
        }}
      >
        <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <CollapseButton onClick={toggleSidebarCollapse} size="small">
            {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </CollapseButton>
        </Tooltip>
        <Scrollbar>
          <Box
            sx={{
              mt: 2,
              mb: 1,
              px: sidebarCollapsed ? 1 : 2,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Logo collapsed={sidebarCollapsed} />
          </Box>
          <Divider
            sx={{
              mt: theme.spacing(1),
              mx: theme.spacing(sidebarCollapsed ? 1 : 2),
              background: theme.colors.alpha.trueWhite[10]
            }}
          />
          <SidebarMenu collapsed={sidebarCollapsed} />
        </Scrollbar>
        {!sidebarCollapsed && (
          <>
            <Divider
              sx={{
                background: theme.colors.alpha.trueWhite[10]
              }}
            />
            <Box p={1.5}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.colors.alpha.trueWhite[50],
                    fontSize: '0.65rem'
                  }}
                >
                  Powered by
                </Typography>
                <Box
                  onMouseEnter={handleClick}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Box
                    component="img"
                    src="/static/images/bayg-watermark.png"
                    alt="BAYG Media"
                    sx={{
                      maxWidth: '80px',
                      width: 'auto',
                      height: 'auto',
                      opacity: 0.6,
                      filter: 'brightness(0) invert(1)',
                      transition: 'opacity 0.3s',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  />
                </Box>
                <Menu
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                  onMouseLeave={handleClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                  }}
                  transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                  }}
                  PaperProps={{
                    sx: {
                      backgroundColor: theme.colors.alpha.white[100],
                      mt: 1,
                      minWidth: 'auto'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1, p: 1 }}>
                    <Tooltip title="Website">
                      <IconButton
                        component={Link}
                        href="https://www.audome.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleClose}
                        size="small"
                        sx={{
                          color: theme.colors.alpha.trueWhite[100],
                          '&:hover': {
                            backgroundColor: theme.colors.alpha.trueWhite[10]
                          }
                        }}
                      >
                        <LanguageIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Instagram">
                      <IconButton
                        component={Link}
                        href="https://www.instagram.com/audome.io"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleClose}
                        size="small"
                        sx={{
                          color: theme.colors.alpha.trueWhite[100],
                          '&:hover': {
                            backgroundColor: theme.colors.alpha.trueWhite[10]
                          }
                        }}
                      >
                        <InstagramIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Menu>
              </Box>
            </Box>
          </>
        )}
      </SidebarWrapper>
      <Drawer
        sx={{
          boxShadow: `${theme.sidebar.boxShadow}`
        }}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
      >
        <SidebarWrapper
          sx={{
            background:
              theme.palette.mode === 'dark'
                ? theme.colors.alpha.white[100]
                : darken(theme.colors.alpha.black[100], 0.5)
          }}
        >
          <Scrollbar>
            <Box mt={2} mb={1}>
              <Box
                mx={2}
                sx={{
                  width: 52
                }}
              >
                <Logo />
              </Box>
            </Box>
            <Divider
              sx={{
                mt: theme.spacing(1),
                mx: theme.spacing(2),
                background: theme.colors.alpha.trueWhite[10]
              }}
            />
            <SidebarMenu />
          </Scrollbar>
          <Divider
            sx={{
              background: theme.colors.alpha.trueWhite[10]
            }}
          />
          <Box p={1.5}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.colors.alpha.trueWhite[50],
                  fontSize: '0.65rem'
                }}
              >
                Powered by
              </Typography>
              <Box
                onMouseEnter={handleClickDrawer}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box
                  component="img"
                  src="/static/images/bayg-watermark.png"
                  alt="BAYG Media"
                  sx={{
                    maxWidth: '80px',
                    width: 'auto',
                    height: 'auto',
                    opacity: 0.6,
                    filter: 'brightness(0) invert(1)',
                    transition: 'opacity 0.3s',
                    '&:hover': {
                      opacity: 1
                    }
                  }}
                />
              </Box>
            </Box>
            <Menu
              anchorEl={anchorElDrawer}
              open={openDrawer}
              onClose={handleCloseDrawer}
              onMouseLeave={handleCloseDrawer}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'center'
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: 'center'
              }}
              PaperProps={{
                sx: {
                  backgroundColor: theme.colors.alpha.white[100],
                  mt: 1,
                  minWidth: 'auto'
                }
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, p: 1 }}>
                <Tooltip title="Website">
                  <IconButton
                    component={Link}
                    href="https://www.audome.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleCloseDrawer}
                    size="small"
                    sx={{
                      color: theme.colors.alpha.trueWhite[100],
                      '&:hover': {
                        backgroundColor: theme.colors.alpha.trueWhite[10]
                      }
                    }}
                  >
                    <LanguageIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Instagram">
                  <IconButton
                    component={Link}
                    href="https://www.instagram.com/audome.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleCloseDrawer}
                    size="small"
                    sx={{
                      color: theme.colors.alpha.trueWhite[100],
                      '&:hover': {
                        backgroundColor: theme.colors.alpha.trueWhite[10]
                      }
                    }}
                  >
                    <InstagramIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Menu>
          </Box>
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;
